import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Star, Send } from 'lucide-react';
import { apiBaseUrl } from '../config/runtime';
import { getToken } from '../utils/auth';

interface ReviewFormProps {
  tradeId: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ tradeId, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log(`[ReviewForm] 리뷰 제출: tradeId=${tradeId}, rating=${rating}`);
    setError('');

    if (rating === 0) { setError(t('reviewForm.errRating')); return; }
    if (comment.trim().length < 5) { setError(t('reviewForm.errComment')); return; }

    setLoading(true);
    try {
      const token = getToken();
      await axios.post(apiBaseUrl('/reviews'), { tradeId, rating, comment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[ReviewForm] 리뷰 등록 성공');
      if (onSuccess) onSuccess();
    } catch (err: any) {
      console.error('[ReviewForm] 리뷰 등록 실패:', err);
      setError(err.response?.data?.message || t('reviewForm.submitFailed'));
    } finally {
      setLoading(false);
    }
  };

  const labels: Record<number, string> = {
    1: t('reviewForm.r1'),
    2: t('reviewForm.r2'),
    3: t('reviewForm.r3'),
    4: t('reviewForm.r4'),
    5: t('reviewForm.r5'),
  };

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0' }}>
      <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111', marginBottom: 20, letterSpacing: '-0.3px' }}>
        {t('reviewForm.title')}
      </h3>

      <form onSubmit={handleSubmit}>
        {/* 별점 */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10 }}>{t('reviewForm.rating')}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => { console.log(`[ReviewForm] 별점 선택: ${n}`); setRating(n); }}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(0)}
                style={{
                  background: 'none', border: 'none', padding: '2px', cursor: 'pointer',
                  transition: 'transform 0.1s',
                  transform: hovered >= n || rating >= n ? 'scale(1.15)' : 'scale(1)',
                }}
              >
                <Star
                  size={32}
                  fill={hovered >= n || rating >= n ? '#F59E0B' : 'none'}
                  stroke={hovered >= n || rating >= n ? '#F59E0B' : '#DDD'}
                  strokeWidth={1.5}
                />
              </button>
            ))}
            {(hovered || rating) ? (
              <span style={{ marginLeft: 8, fontSize: 14, color: '#F59E0B', fontWeight: 600 }}>
                {labels[hovered || rating]}
              </span>
            ) : null}
          </div>
        </div>

        {/* 코멘트 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>{t('reviewForm.content')}</div>
          <textarea
            value={comment}
            onChange={e => { console.log('[ReviewForm] 코멘트 입력'); setComment(e.target.value); }}
            placeholder={t('reviewForm.placeholder')}
            rows={4}
            style={{
              width: '100%', padding: '12px 14px',
              border: '1.5px solid #E8E8E8', borderRadius: 10,
              fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
              boxSizing: 'border-box', lineHeight: 1.6,
              transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = '#4F46E5'}
            onBlur={e => e.target.style.borderColor = '#E8E8E8'}
          />
          <div style={{ fontSize: 12, color: '#BBB', marginTop: 4, textAlign: 'right' }}>
            {t('projectDetail.charsN', { n: comment.length })}
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, fontSize: 13, color: '#EF4444', marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {onCancel && (
            <button type="button" onClick={onCancel} style={{
              flex: 1, padding: '12px', background: '#F5F5F5', color: '#666',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              {t('common.cancel')}
            </button>
          )}
          <button type="submit" disabled={loading} style={{
            flex: 2, padding: '12px',
            background: loading ? '#A5B4FC' : '#4F46E5', color: '#fff',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <Send size={15} />
            {loading ? t('svCreate.submitting') : t('reviewForm.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
